import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, errorMessage } from '../../lib/api';
import { authApi } from '../../lib/endpoints';
import { safeNext } from './AuthGate';
import { AuthLayout } from './AuthLayout';
import { DiscordButton } from './LoginScreen';
import { completeLogin } from './session';
import { useAuthConfig } from './useAuthConfig';

export const USERNAME_RE = /^[a-z0-9_.]{2,32}$/;

export function validateRegistration(v: { username: string; password: string; password2: string; email: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!USERNAME_RE.test(v.username)) errors.username = '2–32 Zeichen: Kleinbuchstaben, Ziffern, Punkt, Unterstrich.';
  if (v.password.length < FIXED_LIMITS.passwordMin) errors.password = `Mindestens ${FIXED_LIMITS.passwordMin} Zeichen.`;
  if (v.password.length > FIXED_LIMITS.passwordMax) errors.password = `Höchstens ${FIXED_LIMITS.passwordMax} Zeichen.`;
  if (v.password2 !== v.password) errors.password2 = 'Passwörter stimmen nicht überein.';
  if (v.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) errors.email = 'Ungültige E-Mail-Adresse.';
  return errors;
}

export function RegisterScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const config = useAuthConfig();
  const [v, setV] = useState({ username: '', displayName: '', email: '', password: '', password2: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV({ ...v, [k]: k === 'username' ? e.target.value.toLowerCase() : e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validateRegistration(v);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.register({
        username: v.username,
        password: v.password,
        email: v.email || undefined,
        displayName: v.displayName.trim() || undefined,
      });
      completeLogin(res);
      void navigate(safeNext(params.get('next')), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'USERNAME_TAKEN') setErrors({ username: 'Benutzername ist vergeben.' });
      else if (err instanceof ApiError && err.code === 'EMAIL_TAKEN') setErrors({ email: 'E-Mail wird bereits verwendet.' });
      else if (err instanceof ApiError && err.code === 'REGISTRATION_CLOSED') setError('Die Registrierung ist geschlossen.');
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!config.registrationOpen) {
    return (
      <AuthLayout title="Registrierung geschlossen" testId="register-closed">
        <p className="muted">Neue Konten legen derzeit nur die Admins an.</p>
        <Link className="btn btn-primary btn-block" to="/login">
          Zur Anmeldung
        </Link>
      </AuthLayout>
    );
  }

  const field = (k: keyof typeof v, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div className="field">
      <label htmlFor={`reg-${k}`}>{label}</label>
      <input
        id={`reg-${k}`}
        className="input"
        value={v[k]}
        onChange={set(k)}
        aria-invalid={!!errors[k]}
        aria-describedby={errors[k] ? `reg-${k}-err` : undefined}
        data-testid={`register-${k}`}
        {...props}
      />
      {errors[k] && (
        <span id={`reg-${k}-err`} className="error-text">
          {errors[k]}
        </span>
      )}
    </div>
  );

  return (
    <AuthLayout title="Konto erstellen" subtitle="Ein Platz am Feuer ist frei." testId="register-screen">
      <form onSubmit={submit} noValidate>
        {field('username', 'Benutzername', { autoComplete: 'username', autoFocus: true, maxLength: 32 })}
        {field('displayName', 'Anzeigename (optional)', { maxLength: FIXED_LIMITS.displayNameMax })}
        {field('email', 'E-Mail (optional)', { type: 'email', autoComplete: 'email' })}
        {field('password', 'Passwort', { type: 'password', autoComplete: 'new-password' })}
        {field('password2', 'Passwort wiederholen', { type: 'password', autoComplete: 'new-password' })}
        {error && (
          <p className="error-text" role="alert" data-testid="register-error">
            {error}
          </p>
        )}
        <button className="btn btn-primary btn-block" disabled={busy} data-testid="register-submit">
          {busy ? 'Wird erstellt …' : 'Konto erstellen'}
        </button>
        {config.discordLogin && <DiscordButton label="Mit Discord registrieren" />}
      </form>
      <p className="auth-footer">
        Schon ein Konto? <Link to="/login">Anmelden</Link>
      </p>
    </AuthLayout>
  );
}
